import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Send, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { supabase } from "@/lib/supabase";

// Edge Function que gera a imagem do story a partir do template + dados do anúncio.
const SHARE_IMAGE_URL =
  "https://zmtqkgtavnzkxxcebzxm.supabase.co/functions/v1/share-image";

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export default function ShareCard({
  open,
  onClose,
  title,
  category,
  image,
  shareUrl,
  listingId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  category: string;
  image?: string;
  shareUrl?: string;
  listingId?: string;
}) {
  const [objectUrl, setObjectUrl] = useState<string>(""); // para a prévia (<img>)
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null); // para compartilhar/baixar
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !listingId) return;
    let active = true;
    let createdUrl = "";
    setLoading(true);
    setError(false);
    setObjectUrl("");
    setBuffer(null);

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        const res = await fetch(
          `${SHARE_IMAGE_URL}?id=${encodeURIComponent(listingId)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const buf = await blob.arrayBuffer();
        if (!active) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
        setBuffer(buf);
      } catch (e) {
        if (active) {
          setError(true);
          toast({ description: "Erro ao gerar a imagem do anúncio.", variant: "destructive" });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listingId]);

  function download() {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `agroaluga-anuncio-${listingId ?? ""}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function shareImg() {
    if (!buffer) return;

    // App nativo: grava o PNG e abre a folha nativa (Instagram / Stories / etc)
    if (Capacitor.isNativePlatform()) {
      try {
        const base64 = arrayBufferToBase64(buffer);
        const fileName = `agroaluga-${Date.now()}.png`;
        const saved = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: "AgroAluga",
          text: shareUrl ? `${title}\n\nVeja o anúncio: ${shareUrl}` : title,
          url: saved.uri,
          dialogTitle: "Compartilhar anúncio",
        });
      } catch {
        toast({ description: "Não foi possível compartilhar a imagem.", variant: "destructive" });
      }
      return;
    }

    // Navegador: Web Share API com arquivo, senão download
    try {
      const blob = new Blob([buffer], { type: "image/png" });
      const file = new File([blob], "agroaluga-anuncio.png", { type: "image/png" });
      // @ts-ignore
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "AgroAluga", text: shareUrl ? `${title}\n${shareUrl}` : title });
      } else {
        download();
      }
    } catch {
      download();
    }
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 16, padding: 20,
          width: "100%", maxWidth: 360, maxHeight: "90vh", overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong style={{ fontSize: 16 }}>Compartilhar no Instagram</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div style={{ width: "100%", aspectRatio: "1080/1920", background: "#f1f1f1", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
          {loading ? (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: "#b91c1c", fontSize: 14 }}>
              Não foi possível gerar a imagem. Tente novamente.
            </div>
          ) : (
            objectUrl && <img src={objectUrl} alt="Prévia do anúncio" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={shareImg} style={{ flex: 1 }} disabled={loading || !buffer}>
            <Send className="h-4 w-4 mr-2" /> Compartilhar
          </Button>
          <Button onClick={download} variant="outline" style={{ flex: 1 }} disabled={loading || !objectUrl}>
            <Download className="h-4 w-4 mr-2" /> Baixar
          </Button>
        </div>
      </div>
    </div>
  );
}
